import { __extends } from "tslib";
import { getShadowElement, removeElement } from '@tyler-components-web/core';
import { deepQuerySelectorAll, getActiveElement } from 'shadow-utils';
import { BACKDROP_CONSTANTS } from '../backdrop';
import { CHECKBOX_CONSTANTS } from '../checkbox';
import { toggleClass } from '../core';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { ICON_BUTTON_CONSTANTS } from '../icon-button';
import { RADIO_CONSTANTS } from '../radio';
import { SWITCH_CONSTANTS } from '../switch';
import { DIALOG_CONSTANTS } from './dialog-constants';
/**
 * Provides facilities for interacting with the internal DOM of `DialogComponent`.
 */
var DialogAdapter = /** @class */ (function (_super) {
    __extends(DialogAdapter, _super);
    function DialogAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._backdropElement = getShadowElement(component, DIALOG_CONSTANTS.selectors.BACKDROP);
        _this._containerElement = getShadowElement(component, DIALOG_CONSTANTS.selectors.CONTAINER);
        _this._surfaceElement = getShadowElement(component, DIALOG_CONSTANTS.selectors.SURFACE);
        return _this;
    }
    DialogAdapter.prototype.initializeAccessibility = function () {
        if (!this._component.hasAttribute('role')) {
            this._component.setAttribute('role', 'dialog');
        }
        this._component.setAttribute('aria-modal', 'true');
    };
    DialogAdapter.prototype.setAnimating = function (animating) {
        if (animating) {
            this._containerElement.classList.add(DIALOG_CONSTANTS.classes.ANIMATING);
        }
        else {
            this._containerElement.classList.remove(DIALOG_CONSTANTS.classes.ANIMATING);
        }
    };
    DialogAdapter.prototype.setVisibility = function (visible) {
        if (visible) {
            this._containerElement.classList.add(DIALOG_CONSTANTS.classes.OPEN);
        }
        else {
            this._containerElement.classList.remove(DIALOG_CONSTANTS.classes.OPEN);
        }
    };
    DialogAdapter.prototype.attach = function () {
        document.body.appendChild(this._component);
    };
    DialogAdapter.prototype.detach = function () {
        if (this._activeElement) {
            this._activeElement = undefined;
        }
        removeElement(this._component);
    };
    DialogAdapter.prototype.registerTransitionEndHandler = function (handler) {
        this._surfaceElement.addEventListener('transitionend', handler);
    };
    DialogAdapter.prototype.deregisterTransitionEndHandler = function (handler) {
        this._surfaceElement.removeEventListener('transitionend', handler);
    };
    DialogAdapter.prototype.setDocumentListener = function (type, listener) {
        document.addEventListener(type, listener);
    };
    DialogAdapter.prototype.removeDocumentListener = function (type, listener) {
        document.removeEventListener(type, listener);
    };
    DialogAdapter.prototype.registerBackdropClickHandler = function (handler) {
        this._backdropElement.addEventListener(BACKDROP_CONSTANTS.events.BACKDROP_CLICK, handler);
    };
    DialogAdapter.prototype.deregisterBackdropClickHandler = function (handler) {
        this._backdropElement.removeEventListener(BACKDROP_CONSTANTS.events.BACKDROP_CLICK, handler);
    };
    DialogAdapter.prototype.getOpenDialogs = function (selector) {
        return document.querySelectorAll(selector);
    };
    DialogAdapter.prototype.setBodyAttribute = function (name, value) {
        document.body.setAttribute(name, value);
    };
    DialogAdapter.prototype.removeBodyAttribute = function (name) {
        document.body.removeAttribute(name);
    };
    DialogAdapter.prototype.trySetInitialFocus = function () {
        var elements = deepQuerySelectorAll(this._component, DIALOG_CONSTANTS.selectors.INITIAL_FOCUS);
        if (elements && elements.length) {
            var initialElement = elements[elements.length - 1];
            initialElement.focus();
        }
    };
    DialogAdapter.prototype.isScrollable = function () {
        var contentElement = this._component.querySelector(DIALOG_CONSTANTS.selectors.CONTENT);
        if (contentElement) {
            return contentElement.scrollHeight > contentElement.offsetHeight;
        }
        return false;
    };
    DialogAdapter.prototype.addRootClass = function (name) {
        this._component.classList.add(name);
    };
    DialogAdapter.prototype.setFullscreen = function (value) {
        if (value) {
            this._containerElement.classList.add(DIALOG_CONSTANTS.classes.FULLSCREEN);
        }
        else {
            this._containerElement.classList.remove(DIALOG_CONSTANTS.classes.FULLSCREEN);
        }
    };
    DialogAdapter.prototype.setMoveable = function (value) {
        toggleClass(this._containerElement, value, DIALOG_CONSTANTS.classes.MOVEABLE);
    };
    DialogAdapter.prototype.tryLayoutChildren = function () {
        var layoutChildren = [ICON_BUTTON_CONSTANTS.elementName, SWITCH_CONSTANTS.elementName, CHECKBOX_CONSTANTS.elementName, RADIO_CONSTANTS.elementName];
        var commonLayoutElements = Array.from(this._component.querySelectorAll(layoutChildren.join(',')));
        commonLayoutElements.filter(function (el) { return typeof el.layout === 'function'; }).forEach(function (el) { return el.layout(); });
    };
    DialogAdapter.prototype.setMoveTarget = function (selector) {
        if (!selector) {
            return false;
        }
        this._moveTargetElement = this._component.querySelector(selector);
        return !!this._moveTargetElement;
    };
    DialogAdapter.prototype.setMoveTargetHandler = function (type, listener) {
        if (this._moveTargetElement) {
            this._moveTargetElement.addEventListener(type, listener);
        }
    };
    DialogAdapter.prototype.removeDragTargetHandler = function (type, listener) {
        if (this._moveTargetElement) {
            this._moveTargetElement.removeEventListener(type, listener);
        }
    };
    DialogAdapter.prototype.getSurfaceBounds = function () {
        return this._surfaceElement.getBoundingClientRect();
    };
    DialogAdapter.prototype.setSurfacePosition = function (x, y, positionType) {
        this._surfaceElement.style.position = positionType === 'absolute' ? positionType : 'relative';
        if (y !== null) {
            this._surfaceElement.style.top = y;
        }
        else {
            this._surfaceElement.style.removeProperty('top');
        }
        if (x !== null) {
            this._surfaceElement.style.left = x;
        }
        else {
            this._surfaceElement.style.removeProperty('left');
        }
    };
    DialogAdapter.prototype.captureActiveElement = function () {
        this._activeElement = getActiveElement();
        if (this._activeElement) {
            this._activeElement.blur();
        }
    };
    DialogAdapter.prototype.tryRestoreActiveElement = function () {
        if (this._activeElement) {
            if (this._activeElement.isConnected) {
                this._activeElement.focus();
            }
            this._activeElement = undefined;
        }
    };
    return DialogAdapter;
}(BaseAdapter));
export { DialogAdapter };
